import { useState, useEffect } from "react";
import { orgApi } from "../utils/apiV2";
import api from "../utils/api";  // Keep for file uploads (not migrated yet)
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import ServiceToggle from "./ServiceToggle";
import { Textarea } from "./ui/textarea";
import {
  Plus, Trash2, Package, Edit, CheckSquare, Users, X, Upload,
  Image as ImageIcon, Shield, Loader2, Settings, AlertCircle, Check, Eye, EyeOff,
  LayoutGrid, List, ArrowUp, ArrowDown, Sparkles, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import Pagination from "./Pagination";
import AddAssetModal from "./AddAssetModal";
import TabPageLayout from './TabPageLayout';
import MassActionToolbar from './MassActionToolbar';
import EmptyState from './EmptyState';
import { PrimaryButton, SecondaryButton, LayoutActionButton } from './Buttons';
import StandardModal from './StandardModal';
import ModalTabSelector from './ModalTabSelector';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useRoles } from '../hooks/useData';
import RoleSelector from './RoleSelector';
import ResourceCard from './ResourceCard';
import ImageViewer from './ImageViewer';
import { useOperatorLocale } from '../hooks/useOperatorLocale';

// Compact Active on/off toggle for the asset card header (top-right, next to the title).
// Persists immediately (optimistic) and the AI suggest/auto-assign engine respects it.
const AssetActiveToggle = ({ asset, onRefresh }) => {
  const [active, setActive] = useState(asset.active !== false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setActive(asset.active !== false); }, [asset.active]);

  const save = async (v) => {
    setActive(v);
    setBusy(true);
    try {
      const res = await orgApi.updateAsset(asset.id, { active: v });
      const warn = res?.data?.capacityWarnings;
      if (warn?.count) {
        toast.warning(warn.message, {
          duration: 8000,
          description: `${warn.count} upcoming booking(s) may need re-seating — existing bookings stay honored.`,
        });
      }
      onRefresh?.();
    } catch (e) {
      setActive(!v);
      toast.error("Could not update the boat — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center" onClick={(e) => e.stopPropagation()} data-testid={`asset-active-toggle-${asset.id}`}>
      <ServiceToggle checked={active} disabled={busy} onChange={save} />
    </div>
  );
};

// Sparkle next to the asset title — lit = available for AI Suggested Assign, dim = opted out.
const AssetAiSparkle = ({ aiOn }) => (
  <span
    data-testid="asset-ai-sparkle"
    title={aiOn ? "Available for AI Suggested Assign" : "Not available for AI suggestions"}
    className="flex-shrink-0"
  >
    <Sparkles className={`h-3 w-3 ${aiOn ? "text-blue-600" : "text-gray-300"}`} />
  </span>
);


const AssetsTab = ({ assets, teamMembers, onRefresh, isLoading: propsLoading, isError: propsError }) => {
  const { roles } = useRoles();
  const { formatMoney } = useOperatorLocale();
  const [assetRevenue, setAssetRevenue] = useState({});
  const [revenueLoaded, setRevenueLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTab, setEditTab] = useState("basic");
  const [viewMode, setViewMode] = useState("card");

  // Filter chips — default OFF (hide inactive assets + hide revenue figures); user can toggle on
  const [showInactive, setShowInactive] = useState(false);
  const [showMoney, setShowMoney] = useState(false);

  // Trailing 12-month top-line revenue per asset (lightweight, fetched once)
  useEffect(() => {
    let mounted = true;
    orgApi.getAssetRevenue(12)
      .then((res) => { if (mounted) { setAssetRevenue(res?.revenue || {}); setRevenueLoaded(true); } })
      .catch(() => { if (mounted) setRevenueLoaded(true); });
    return () => { mounted = false; };
  }, []);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: "" });
  const [editingAsset, setEditingAsset] = useState(null);

  // Form states
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("12");
  const [active, setActive] = useState(true);
  const [aiAssignable, setAiAssignable] = useState(true);
  const [notes, setNotes] = useState("");
  const [assetImages, setAssetImages] = useState([]);
  // Documents are no longer edited in the UI but are preserved on save
  const [preservedDocuments, setPreservedDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [teamRequirements, setTeamRequirements] = useState([]);

  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const [viewerTitle, setViewerTitle] = useState("Asset Photos");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Sorting (list view)
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Mass action states
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    setSelectedAssets([]);
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev =>
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const selectAllAssets = () => {
    setSelectedAssets(paginatedAssets.map(asset => asset.id));
  };

  const deselectAllAssets = () => {
    setSelectedAssets([]);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUp className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 text-gray-700" />
      : <ArrowDown className="w-3 h-3 ml-1 text-gray-700" />;
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const deletePromises = selectedAssets.map(assetId =>
        orgApi.deleteAsset(assetId)
      );

      await Promise.all(deletePromises);
      toast.success(`${selectedAssets.length} asset(s) deleted successfully`);
      setSelectedAssets([]);
      setMassActionMode(false);
      setBulkDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      console.error("Error deleting assets:", error);
      toast.error("Failed to delete some assets. They may be assigned to trips.");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleEditClick = (asset) => {
    setEditingAsset(asset);
    setEditTab("basic");
    setName(asset.name);
    setCapacity(asset.capacity?.toString() || "12");
    setActive(asset.active !== undefined ? asset.active : true);
    setAiAssignable(asset.aiAssignable !== undefined ? asset.aiAssignable : !(asset.name || '').toUpperCase().startsWith('ZEXTRA'));
    setNotes(asset.notes || "");
    setAssetImages(asset.photos && asset.photos.length > 0
      ? asset.photos
      : (asset.photoUrl && asset.photoUrl.startsWith('http'))
        ? [asset.photoUrl]
        : []);
    setPreservedDocuments(asset.documents || []);
    setTeamRequirements(asset.teamRequirements || []);
    setOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (assetImages.length + files.length > 5) {
      toast.error("Maximum 5 photos allowed per asset");
      return;
    }

    setUploadingPhoto(true);
    try {
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/upload/asset-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        uploadedUrls.push(response.data.url);
      }

      setAssetImages(prev => [...prev, ...uploadedUrls]);
      toast.success(`${files.length} photo(s) uploaded`);
      if (e.target) e.target.value = '';
    } catch (error) {
      console.error("Error uploading photos:", error);
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = (index) => {
    setAssetImages(prev => prev.filter((_, i) => i !== index));
    toast.success("Photo removed");
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter an asset name");
      return;
    }

    const duplicateAsset = assets.find(a =>
      a.name.toLowerCase().trim() === name.toLowerCase().trim() &&
      a.id !== editingAsset?.id
    );

    if (duplicateAsset) {
      toast.error(`An asset named "${name}" already exists`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        capacity: parseInt(capacity) || 12,
        active,
        excludeFromAi: !aiAssignable,
        notes: notes?.trim() || null,
        photoUrl: assetImages.length > 0 ? assetImages[0] : null,
        photos: assetImages,
        documents: preservedDocuments, // preserved, not editable in UI
        teamRequirements: teamRequirements
      };

      if (editingAsset) {
        const res = await orgApi.updateAsset(editingAsset.id, payload);
        toast.success("Asset updated successfully");
        const warn = res?.data?.capacityWarnings;
        if (warn?.count) {
          toast.warning(warn.message, {
            duration: 10000,
            description: `${warn.count} upcoming booking(s) affected — re-seat them onto a larger / active boat. Existing bookings stay honored.`,
          });
        }
      } else {
        await orgApi.createAsset(payload);
        toast.success("Asset added successfully");
      }
      setOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error("Error saving asset:", error);
      toast.error("Failed to save asset");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (asset) => {
    setDeleteConfirm({ open: true, id: asset.id, name: asset.name });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const res = await orgApi.deleteAsset(deleteConfirm.id);
      toast.success("Asset deleted successfully");
      const warn = res?.data?.capacityWarnings;
      if (warn?.count) {
        toast.warning(warn.message, {
          duration: 10000,
          description: `${warn.count} upcoming booking(s) were on this boat — re-seat them. They remain active until you move them.`,
        });
      }
      setDeleteConfirm({ open: false, id: null, name: "" });
      onRefresh();
    } catch (error) {
      console.error("Error deleting asset:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete asset";

      if (error.response?.status === 400) {
        toast.error(errorMsg, { duration: 6000 });
      } else {
        toast.error("Failed to delete asset");
      }

      setDeleteConfirm({ open: false, id: null, name: "" });
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setEditingAsset(null);
    setEditTab("basic");
    setName("");
    setCapacity("12");
    setActive(true);
    setAiAssignable(true);
    setNotes("");
    setAssetImages([]);
    setPreservedDocuments([]);
    setTeamRequirements([]);
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  // Filter (chips) then sort
  const visibleAssets = (assets || []).filter((a) => showInactive || a.active !== false);
  const sortedAssets = [...visibleAssets].sort((a, b) => {
    let compareValue = 0;
    if (sortField === "name") {
      compareValue = (a.name || '').localeCompare(b.name || '');
    }
    return sortDirection === "asc" ? compareValue : -compareValue;
  });

  // Pagination
  const totalPages = Math.ceil(sortedAssets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAssets = sortedAssets.slice(startIndex, startIndex + itemsPerPage);

  // Error state
  if (propsError) {
    return (
      <TabPageLayout
        title="Assets"
        subtitle="Register your resources"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load assets</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => onRefresh()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Assets"
      subtitle="Register your resources"
      loading={propsLoading}
      actions={
        <div className="flex items-center gap-2">
          <LayoutActionButton icon={Plus} onClick={() => setShowAddModal(true)} data-testid="assets-add-button">
            Add
          </LayoutActionButton>
        </div>
      }
    >
      <div data-testid="assets-tab" className="space-y-6">
        <AddAssetModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={onRefresh}
          teamMembers={teamMembers}
        />

        {/* Edit Asset Modal */}
        {editingAsset && (
          <StandardModal
            isOpen={open}
            onClose={handleOpenChange}
            title="Edit Asset"
            description={`Update ${editingAsset.name}'s details`}
            size="md"
            footer={
              <>
                <SecondaryButton onClick={() => handleOpenChange(false)} className="flex-1 sm:flex-none" disabled={saving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleSubmit} disabled={saving} className="flex-1 sm:flex-none" data-testid="edit-asset-save-button">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </PrimaryButton>
              </>
            }
          >
            <div className="mb-5">
              <ModalTabSelector
                tabs={[
                  { value: "basic", label: "Basic", icon: Package },
                  { value: "team", label: "Team", icon: Users },
                  { value: "photos", label: "Photos", icon: ImageIcon },
                ]}
                value={editTab}
                onChange={setEditTab}
              />
            </div>

            {/* Tab: Basic Info */}
            {editTab === "basic" && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="asset-name">Asset Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="asset-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Sea Breeze"
                    className={`mt-1.5 ${!name.trim() && name !== '' ? 'border-red-300 focus:border-red-500' : ''}`}
                    data-testid="edit-asset-name-input"
                  />
                  {!name.trim() && name !== '' && (
                    <p className="text-sm text-red-500 mt-1">Asset name is required</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="asset-capacity">Capacity <span className="text-red-500">*</span></Label>
                    <Input
                      id="asset-capacity"
                      type="number"
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                      placeholder="12"
                      className="mt-1.5"
                      data-testid="edit-asset-capacity-input"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-7">
                    <Label>Active {!active && <span className="text-xs text-red-500 font-normal">(won't be assigned to trips)</span>}</Label>
                    <ServiceToggle
                      checked={active}
                      onChange={setActive}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <Label>Available for AI Suggested Assign</Label>
                    <p className="mt-0.5 text-xs font-normal text-gray-400">
                      When off, the AI never suggests or auto-assigns this boat (handy for placeholder assets).
                    </p>
                  </div>
                  <ServiceToggle
                    checked={aiAssignable}
                    onChange={setAiAssignable}
                  />
                </div>

                <div>
                  <Label htmlFor="asset-notes">Notes (Optional)</Label>
                  <Textarea
                    id="asset-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., Under maintenance until Dec 15th"
                    className="mt-1.5"
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Tab: Team Requirements */}
            {editTab === "team" && (
              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4" />
                    Team Requirements
                  </Label>
                  <p className="text-sm text-gray-500 mb-4">
                    Define which roles are needed to operate this asset
                  </p>

                  <div className="space-y-2">
                    {teamRequirements.length === 0 ? (
                      <div className="text-center py-6 bg-gray-50 rounded-xl">
                        <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 mb-3">No team requirements set</p>
                        <SecondaryButton
                          onClick={() => setTeamRequirements([{ roleIds: [''], quantity: 1 }])}
                          size="sm"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Role Requirement
                        </SecondaryButton>
                      </div>
                    ) : (
                      <>
                        {teamRequirements.map((requirement, index) => {
                          // Normalise legacy { roleId } → { roleIds }
                          const roleIds = requirement.roleIds || (requirement.roleId ? [requirement.roleId] : ['']);
                          const primaryRole = roles.find(r => r.id === roleIds[0]);

                          return (
                            <div
                              key={index}
                              className="p-3 bg-gray-50 rounded-xl space-y-2"
                              style={{ borderLeft: `4px solid ${primaryRole?.color || '#E5E7EB'}` }}
                            >
                              {/* Role slots — vertical stack with OR dividers */}
                              <div className="space-y-1.5">
                                {roleIds.map((rid, rIdx) => (
                                  <div key={rIdx} className="space-y-1">
                                    {rIdx > 0 && (
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 border-t border-gray-200" />
                                        <span className="text-[10px] font-semibold text-blue-500 px-1">OR</span>
                                        <div className="flex-1 border-t border-gray-200" />
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1.5">
                                      <RoleSelector
                                        roles={roles}
                                        value={rid}
                                        onChange={(roleId) => {
                                          const updated = [...teamRequirements];
                                          const newIds = [...roleIds];
                                          newIds[rIdx] = roleId;
                                          updated[index] = { ...requirement, roleIds: newIds };
                                          setTeamRequirements(updated);
                                        }}
                                        placeholder="Select role..."
                                        className="flex-1"
                                      />
                                      {roleIds.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updated = [...teamRequirements];
                                            updated[index] = { ...requirement, roleIds: roleIds.filter((_, i) => i !== rIdx) };
                                            setTeamRequirements(updated);
                                          }}
                                          className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...teamRequirements];
                                    updated[index] = { ...requirement, roleIds: [...roleIds, ''] };
                                    setTeamRequirements(updated);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors w-full text-center"
                                >
                                  + Add OR alternative
                                </button>
                              </div>

                              {/* Count + delete */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">Qty:</span>
                                  <div className="flex items-center gap-1 bg-white rounded border">
                                    <button type="button" onClick={() => {
                                      const updated = [...teamRequirements];
                                      updated[index].quantity = Math.max(1, (requirement.quantity || 1) - 1);
                                      setTeamRequirements(updated);
                                    }} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded text-xs">−</button>
                                    <span className="w-5 text-center text-sm font-medium">{requirement.quantity || 1}</span>
                                    <button type="button" onClick={() => {
                                      const updated = [...teamRequirements];
                                      updated[index].quantity = Math.min(10, (requirement.quantity || 1) + 1);
                                      setTeamRequirements(updated);
                                    }} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded text-xs">+</button>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setTeamRequirements(teamRequirements.filter((_, i) => i !== index))}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        <button
                          type="button"
                          onClick={() => setTeamRequirements([...teamRequirements, { roleIds: [''], quantity: 1 }])}
                          className="w-full py-2 text-sm font-medium text-gray-600 border border-dashed border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add Another Role
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Asset Photos */}
            {editTab === "photos" && (
              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <ImageIcon className="w-4 h-4" />
                    Asset Photos ({assetImages.length}/5)
                  </Label>

                  {assetImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {assetImages.map((url, index) => (
                        <div key={index} className="relative group aspect-square rounded-xl overflow-hidden">
                          <img
                            src={url}
                            alt={`Asset ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer"
                            onClick={() => {
                              setViewerImages(assetImages);
                              setViewerInitialIndex(index);
                              setViewerTitle(`${name || 'Asset'} Photos`);
                              setImageViewerOpen(true);
                            }}
                          />
                          {/* View button */}
                          <button
                            onClick={() => {
                              setViewerImages(assetImages);
                              setViewerInitialIndex(index);
                              setViewerTitle(`${name || 'Asset'} Photos`);
                              setImageViewerOpen(true);
                            }}
                            className="absolute bottom-1 left-1 p-1.5 bg-black/70 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            title="View fullscreen"
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                          {/* Remove button */}
                          <button
                            onClick={() => handleRemovePhoto(index)}
                            className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {assetImages.length < 5 && (
                    <div>
                      <input
                        type="file"
                        id="asset-photos"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <SecondaryButton
                        className="w-full"
                        disabled={uploadingPhoto}
                        onClick={() => document.getElementById('asset-photos').click()}
                      >
                        {uploadingPhoto ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Photos ({5 - assetImages.length} remaining)
                          </>
                        )}
                      </SecondaryButton>
                      <p className="text-xs text-gray-500 mt-1">
                        Accepted: JPG, PNG, WEBP • Max 5 photos • Tap to view fullscreen
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </StandardModal>
        )}

        {/* Filter chips — toggle inactive assets + revenue visibility (both on by default) */}
        {assets && assets.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="assets-filter-chips">
            <button
              onClick={() => { setShowInactive((v) => !v); setCurrentPage(1); }}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${showInactive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              data-testid="assets-chip-show-inactive"
            >
              {showInactive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {showInactive ? 'Showing inactive' : 'Hiding inactive'}
            </button>
            <button
              onClick={() => setShowMoney((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${showMoney ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              data-testid="assets-chip-show-money"
            >
              <DollarSign className="w-3.5 h-3.5" />
              {showMoney ? 'Showing revenue' : 'Hiding revenue'}
            </button>
          </div>
        )}

        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedAssets.length}
            onSelectAll={selectAllAssets}
            onDeselectAll={deselectAllAssets}
            onDelete={() => setBulkDeleteConfirm(true)}
            itemName="asset"
          />
        )}

        {/* Content */}
        {!assets || assets.length === 0 ? (
          <EmptyState
            icon={Settings}
            title="No Assets Added Yet"
            description="Click 'Add' to register your first asset"
            actionLabel="Add New Asset"
            onAction={() => setShowAddModal(true)}
          />
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedAssets.map((asset) => {
              const isSelected = selectedAssets.includes(asset.id);

              let iconColor = asset.active ? '#10B981' : '#6B7280';
              if (asset.teamRequirements && asset.teamRequirements.length > 0) {
                const req0 = asset.teamRequirements[0];
                const primaryId = (req0.roleIds && req0.roleIds[0]) || req0.roleId;
                const firstRole = roles.find(r => r.id === primaryId);
                if (firstRole) iconColor = firstRole.color;
              }

              return (
                <ResourceCard
                  key={asset.id}
                  id={asset.id}
                  name={asset.name}
                  icon={Package}
                  iconColor={iconColor}
                  description={undefined}
                  descriptionLines={[
                    { icon: <Users className="w-3 h-3" />, text: `Capacity: ${asset.capacity || 12}` },
                    { icon: <Shield className="w-3 h-3" />, text: asset.teamRequirements?.length
                        ? `${asset.teamRequirements.length} role${asset.teamRequirements.length > 1 ? 's' : ''} required`
                        : 'No team required'
                    },
                  ]}
                  showSeeMore={false}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={() => toggleAssetSelection(asset.id)}
                  additionalClassNames={!asset.active ? 'opacity-60' : ''}
                  titleSuffix={<AssetAiSparkle aiOn={asset.aiAssignable !== false} />}
                  headerAction={<AssetActiveToggle asset={asset} onRefresh={onRefresh} />}
                  metric={revenueLoaded && showMoney
                    ? { label: 'Revenue · last 12 mo', value: formatMoney(assetRevenue[asset.id] || 0) }
                    : undefined}
                  onEdit={() => handleEditClick(asset)}
                  onDelete={() => handleDeleteClick(asset)}
                />
              );
            })}
          </div>
        ) : (
          <div className="card-soft overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[768px]">
                <thead className="bg-gray-50/60 border-b border-gray-100">
                  <tr>
                    {massActionMode && (
                      <th className="w-12 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedAssets.length === paginatedAssets.length && paginatedAssets.length > 0}
                          onChange={(e) => e.target.checked ? selectAllAssets() : deselectAllAssets()}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button onClick={() => handleSort("name")} className="flex items-center hover:text-gray-700">
                        Name {getSortIcon("name")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Team Requirements
                      </div>
                    </th>
                    {showMoney && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue · 12 mo
                      </th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedAssets.map((asset) => {
                    const isSelected = selectedAssets.includes(asset.id);
                    return (
                      <tr key={asset.id} className={`${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}>
                        {massActionMode && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleAssetSelection(asset.id)}
                              className="w-4 h-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-100">
                              <Package className="w-5 h-5 text-blue-600" />
                            </div>
                            <span className="font-medium text-gray-900">{asset.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {asset.teamRequirements && asset.teamRequirements.length > 0 ? (
                              asset.teamRequirements.map((requirement, index) => (
                                <Badge
                                  key={index}
                                  className="text-xs text-white"
                                  style={{
                                    backgroundColor: roles.find(r => r.id === ((requirement.roleIds && requirement.roleIds[0]) || requirement.roleId))?.color || '#3B82F6'
                                  }}
                                >
                                  {requirement.quantity} {requirement.roleName || roles.find(r => r.id === ((requirement.roleIds && requirement.roleIds[0]) || requirement.roleId))?.name || 'role'}
                                </Badge>
                              ))
                            ) : (
                              <Badge variant="outline" className="text-xs text-gray-500">
                                <Check className="w-3 h-3 mr-1" />
                                No team required
                              </Badge>
                            )}
                          </div>
                        </td>
                        {showMoney && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span className="text-sm font-normal" style={{ color: revenueLoaded ? '#2563EB' : '#9CA3AF' }}>
                              {revenueLoaded ? formatMoney(assetRevenue[asset.id] || 0) : '—'}
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(asset)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(asset)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {sortedAssets.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={sortedAssets.length}
          />
        )}

        {/* Delete Asset Modal */}
        <DeleteConfirmModal
          isOpen={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, id: null, name: "" })}
          onConfirm={handleDeleteConfirm}
          title="Delete Asset?"
          description={`Are you sure you want to delete ${deleteConfirm.name}?`}
          isDeleting={isDeleting}
          confirmLabel="Delete Asset"
        >
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>This asset will be unassigned from all future trips.</p>
        </DeleteConfirmModal>

        {/* Bulk Delete Modal */}
        <DeleteConfirmModal
          isOpen={bulkDeleteConfirm}
          onClose={() => setBulkDeleteConfirm(false)}
          onConfirm={handleBulkDelete}
          title="Delete Multiple Assets?"
          description={`Are you sure you want to delete ${selectedAssets.length} asset(s)?`}
          isDeleting={isBulkDeleting}
          confirmLabel={`Delete ${selectedAssets.length} Assets`}
        >
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>Assets assigned to trips may fail to delete.</p>
        </DeleteConfirmModal>

        {/* Fullscreen Image Viewer */}
        <ImageViewer
          isOpen={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          images={viewerImages}
          initialIndex={viewerInitialIndex}
          title={viewerTitle}
        />
      </div>
    </TabPageLayout>
  );
};

export default AssetsTab;
